import { computed, reactive, ref, watch, type Ref } from "vue";
import { OperationInput, CognitoUserCountInput, CognitoUserListInput } from "@/generated/graphql";
import { localStorageGetChainCode, localStorageGetCustomerCode, localStorageGetUserId } from "@/common/localStorageUtil";
import { displayPagingRange as calcDisplayPagingRange } from "@/common/common";
import { GeneralTypeList } from "@/common/constant";
import { listCognitoUser } from "@/common/graphql/cognito";
import type { ListData, CognitoUserRecord } from "../types";

/**
 * 一覧テーブル表示に必要なデータ整形と列情報を提供する composable。
 *
 * @returns テーブル描画に利用する列定義と表示用データ
 */
export const useOperatorListTable = (
  chainViewFlag: boolean,
  chainSelectValues: Ref<{ chain_code: string; name: string } | null>,
  shopSelectValues: Ref<{ shop_code: string; name: string } | null>,
  groupSelectValues: Ref<{ index: string; item: string } | null>,
  authtypeSelectValues: Ref<{ index: string; item: string } | null>,
  searchConditionId: Ref<string>,
  searchConditionName: Ref<string>,
  errdialog: Ref<boolean>,
  errMessage: Ref<string>,
  groupRadioItemsAll: Array<{ index: string; item: string }>,
  authtypeRadioItemsAll: Array<{ index: string; item: string }>,
  isUpdateModeBack: () => boolean,
) => {
  //////////////////////////////////////////////////////////////////////////////
  // 一覧表示
  //////////////////////////////////////////////////////////////////////////////
  const tableHeaders = [
    { title: "店舗", align: "center", sortable: false, key: "shopName" },
    { title: "所属グループ", align: "center", sortable: false, key: "group" },
    { title: "権限", align: "center", sortable: false, key: "authType" },
    { title: "オペレータID", align: "center", sortable: false, key: "id" },
    { title: "オペレータ名", align: "center", sortable: false, key: "name" },
    { title: "個人情報閲覧", align: "center", sortable: false, key: "personalInfo" },
    { title: "個人情報ダウンロード", align: "center", sortable: false, key: "personalInfoDl" },
    { title: "", align: "center", sortable: false, key: "actions" },
  ];

  const listLoading = ref(false);

  const listOptions = reactive<{ page: number; itemsPerPage: number; sortBy: Array<{ key: string; order: "asc" | "desc" }> }>({
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
  });

  const listDataArray = ref<ListData[]>([]);
  const listDataTotalCount = ref(0);
  const pageCount = computed(() =>
    Math.max(1, Math.ceil(listDataTotalCount.value / (listOptions.itemsPerPage || 1)))
  );
  const selectListCheckbox = ref<ListData[]>([]);

  // ページング範囲の表示
  const displayPagingRange = ref("");

  // deep watch on listOptions
  watch(
    () => listOptions,
    async () => {
      listLoading.value = true;
      await searchQuery();
      listLoading.value = false;
    },
    { deep: true }
  );

  /**
   * オペレータ一覧 オプション更新
   */
  function onUpdateOptions(newOptions: { page: number; itemsPerPage: number; sortBy: Array<{ key: string; order: "asc" | "desc" }> }): void {
    listOptions.page = newOptions.page;
    listOptions.itemsPerPage = newOptions.itemsPerPage;
    listOptions.sortBy = newOptions.sortBy;
  }

  /**
   * 絞り込み検索 @Click
   */
  async function onClickSearch(): Promise<void> {
    if (!(isUpdateModeBack()) && chainViewFlag && !(chainSelectValues.value?.chain_code)) {
      errMessage.value = "チェーンを選択してください";
      errdialog.value = true;
      return;
    }
    if (listOptions.page !== 1) {
      listOptions.page = 1;
    } else {
      await searchQuery();
    }
  }

  /**
   * 絞り込み検索
   */
  async function searchQuery(): Promise<void> {
    try {
      if (chainViewFlag && !(chainSelectValues.value?.chain_code)) {
        return;
      }
      listLoading.value = true;
      const selectedGroupIndex = groupSelectValues.value?.index ?? "";
      const selectedAuthTypeIndex = authtypeSelectValues.value?.index ?? "";

      const listInput: CognitoUserListInput = {
        chain_code: chainViewFlag
          ? chainSelectValues.value?.chain_code
            ? chainSelectValues.value.chain_code
            : ""
          : localStorageGetChainCode(),
        id: searchConditionId.value && searchConditionId.value !== "" ? searchConditionId.value : undefined,
        name: searchConditionName.value && searchConditionName.value !== "" ? searchConditionName.value : undefined,
        group: selectedGroupIndex !== "" ? selectedGroupIndex : undefined,
        auth_type: selectedAuthTypeIndex !== "" ? selectedAuthTypeIndex : undefined,
        shop_code:
          shopSelectValues.value &&
          shopSelectValues.value.shop_code &&
          shopSelectValues.value.shop_code !== ""
            ? shopSelectValues.value.shop_code
            : undefined,
        customer_code: localStorageGetCustomerCode(),
        limit: listOptions.itemsPerPage,
        offset: (listOptions.page - 1) * listOptions.itemsPerPage,
      };
      const operationInput: OperationInput = {
        area_code: "10",
        detail: "操作詳細",
        display_category: "オペレータ一覧",
        operation: "一覧表示",
        user_id: localStorageGetUserId(),
      };
      const countInput: CognitoUserCountInput = {
        auth_type: listInput.auth_type,
        chain_code: listInput.chain_code,
        customer_code: listInput.customer_code,
        group: listInput.group,
        id: listInput.id,
        name: listInput.name,
        shop_code: listInput.shop_code,
      };
      const listResult = await listCognitoUser(listInput, operationInput, countInput);
      const userArray: CognitoUserRecord[] = listResult.listCognitoUser ?? [];
      const _listDataArray: ListData[] = [];
      userArray.forEach(value => {
        if (!value) return;
        const personalInfo = GeneralTypeList.possible.find((element: { index: string; item: string }) => element.index === value.personal_info)?.item;
        const personalInfoDl = GeneralTypeList.possible.find((element: { index: string; item: string }) => element.index === value.personal_info_dl)?.item;
        const group = groupRadioItemsAll.find((element: { index: string; item: string }) => element.index === value.group)?.item;
        const authType = authtypeRadioItemsAll.find((element: { index: string; item: string }) => element.index === value.auth_type)?.item;
        _listDataArray.push({
          chainCode: chainSelectValues.value?.chain_code ? chainSelectValues.value.chain_code : "",
          id: value.user_id,
          name: value.name.length > 10 ? value.name.substr(0, 15) + "..." : value.name,
          group: group ? group : "",
          authType: authType ? authType : "",
          shopName: value.shop_name!,
          personalInfo: personalInfo ? personalInfo : "",
          personalInfoDl: personalInfoDl ? personalInfoDl : "",
        });
      });
      listDataArray.value = _listDataArray;
      listDataTotalCount.value = listResult.getCognitoUserCount?.count ? listResult.getCognitoUserCount.count : 0;
      const maxPage = pageCount.value;
      if (listOptions.page > maxPage) {
        listOptions.page = maxPage;
      }
      displayPagingRange.value = calcDisplayPagingRange(listOptions.page - 1, listOptions.itemsPerPage, listDataTotalCount.value);
    } catch (err) {
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "不明なエラーが発生しました。";
        console.error("未知のエラー:", err);
      }
      return;
    } finally {
      listLoading.value = false;
    }
  }

  return {
    tableHeaders,
    listLoading,
    listOptions,
    listDataArray,
    listDataTotalCount,
    pageCount,
    selectListCheckbox,
    displayPagingRange,
    onUpdateOptions,
    onClickSearch,
    searchQuery,
  };
};
